
import {
  Device,
  FuelConsumptionType,
  FuelUnit,
  SmartConnectVehicle,
  SmartConnectVehicleOdometerTypeEnum,
  TripLog
} from '@clients/myTrips';
import AppContent from '@components/new-styleguide/AppContent.vue';
import AppHeader from '@components/new-styleguide/AppHeader.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import NumberInput from '@components/new-styleguide/form/NumberInput.vue';
import GlobalVehicleSelect from '@components/new-styleguide/GlobalVehicleSelect.vue';
import TooltipElement from '@components/new-styleguide/TooltipElement.vue';
import InformationLines, { InformationDetail } from '@components/settings/information/InformationLines.vue';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import AppListItem from '@components/new-styleguide/AppListItem.vue';
import { formatLocale } from '@util/numberFormat';
import { LoadState } from '@util/types';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const TriplogModule = namespace('Triplog');

@Component({
  name: 'InformationOverview',
  components: {
    TooltipElement,
    InformationLines,
    NumberInput,
    InfoBoxWarning,
    GlobalVehicleSelect,
    AppContent,
    AppHeader,
    AppPage,
    AppListItem
  }
})
export default class InformationOverview extends Vue {
  @TriplogModule.Getter('getSelectedTriplog')
  private selectedTriplog: TripLog;

  @TriplogModule.Getter('getVehiclesStatus')
  private triplogsStatus: LoadState;

  @TriplogModule.Action('fetchDevice')
  private fetchDevice;

  @TriplogModule.Getter('getDevice')
  private device: Device;

  @TriplogModule.Getter('getDeviceStatus')
  private deviceStatus: LoadState;

  @TriplogModule.Action('fetchSmartConnectVehicles')
  private fetchSmartConnectVehicles;

  @TriplogModule.Getter('getSmartConnectVehicles')
  private smartConnectVehicles: Array<SmartConnectVehicle>;

  @TriplogModule.Getter('getSmartConnectVehiclesStatus')
  private smartConnectVehiclesStatus: LoadState;

  @TriplogModule.Action('fetchFitment')
  private fetchFitment;

  @TriplogModule.Action('showAllSuggestedFuelStops')
  private showAllSuggestedFuelStops;

  @TriplogModule.Action('setFuelStopSuggestions')
  private setFuelStopSuggestions;

  private showAllSuggestionsDisabled: boolean = false;
  private suggestionsDisabled: boolean = false;
  private errorMessage: string = null;

  async created() {
    if (this.selectedTriplog && this.deviceIdExists) {
      this.fetchDevice({ deviceId: this.selectedTriplog.vehicle.deviceId })
    }
    this.fetchSmartConnectVehicles();
  }

  get informationList(): InformationDetail[] {
    if (!this.selectedTriplog) {
      return [];
    }

    const informationList: InformationDetail[] = [];

    if (this.deviceIdExists) {
      informationList.push({
        type: 'serial',
        name: 'Seriennummer',
        value: this.serialNumber
      });
    }

    if (this.selectedTriplog.smartConnected) {
      informationList.push(
        {
          type: 'odometer',
          name: 'Kilometerstand',
          value: this.smartConnectOdometer,
          unit: this.odometerType,
          editable: this.odometerEditable
        },
        {
          type: 'fuelConsumption',
          name: 'Verbrauchswerte',
          value: this.fuelConsumptionType,
          editable: true,
          tooltip: 'Bei "Gemessen" wird der Verbrauch vom Gerät im Auto gemessen, bei "Berechnet" wird es anhand der ' +
            'zurückgelegten Strecke annähernd berechnet. Wenn der gemessene Wert ungenau ist, können Sie die Option ' +
            '"Berechnet" probieren.'
        }
      );
    } else {
      informationList.push({
          type: 'odometer',
          name: 'Kilometerstand',
          value: this.odometer,
          unit: this.selectedTriplog.odometer ? 'km' : ''
      });
    }

    informationList.push(
      {
        type: 'fuelUnit',
        name: 'Einheit Verbrauch',
        value: this.fuelUnit,
        editable: true
      },
      {
        type: 'fuelCapacity',
        name: 'Tankvolumen',
        value: this.volume,
        unit: this.fuelUnit,
        editable: true
      },
      {
        type: 'suggestions',
        name: 'Tankvorschläge anzeigen',
        value: String(this.suggestionsChecked),
        editable: true,
        switch: true
      },
      {
        type: 'mergeBreakThreshold',
        name: 'Merge-Pausen (10m - 1d)',
        value: String(this.mergeBreakThreshold),
        editable: true,
      }
    );

    if (this.selectedTriplog.smartConnected) {
      informationList.push({
        type: 'setup',
        name: 'Einbauanleitung',
        value: 'Öffnen',
        link: true
      });
    }

    return informationList;
  }

  get tooltip() {
    return 'Wenn Sie bei einigen Vorschlägen zu Betankungen auf "Nein" gedrückt haben, können Sie mit dieser Funktion alle ' +
      'Tankvorschläge wieder anzeigen lassen.'
  }

  get suggestionsChecked() {
    return this.selectedTriplog.vehicleSettings.fuelStopSuggestions;
  }

  get mergeBreakThreshold() {
    return this.selectedTriplog.vehicleSettings.mergeBreakThreshold ?? "10m";
  }

  get showAllSuggestionsValue() {
    return { title: 'Versteckte Tankvorschläge wieder anzeigen', hasButton: true };
  }

  get toggleFuelStopSuggestions() {
    return { title: 'Tankvorschläge aktivieren', hasButton: true }
  }

  get odometer() {
    return this.selectedTriplog.odometer ? String(formatLocale(this.selectedTriplog.odometer / 1000, 1)) : '-';
  }

  get volume() {
    if (!this.selectedTriplog.vehicleSettings.fuelCapacity) {
      return '0';
    }
    return String(formatLocale(this.selectedTriplog.vehicleSettings.fuelCapacity, 1));
  }

  get serialNumber() {
    if (this.serialLoading) {
      return undefined;
    }
    return this.device.serialNumber;
  }

  get odometerEditable() {
    if (!this.smartConnectVehiclesLoading) {
      let smartConnect = this.smartConnectVehicles.find(o => o.vehicleId === this.selectedTriplog.vehicle.id);
      return smartConnect ? smartConnect.odometerType === 'gps' : false;
    }
    return false;
  }

  get smartConnectOdometer() {
    if (!this.smartConnectVehiclesLoading) {
      let smartConnect = this.smartConnectVehicles.find(o => o.vehicleId === this.selectedTriplog.vehicle.id);
      return String(formatLocale(smartConnect.odometer / 1000, 1));
    }
    return undefined;
  }

  get odometerType() {
    if (this.smartConnectOdometer) {
      if (!this.smartConnectVehiclesLoading) {
        let smartConnect = this.smartConnectVehicles.find(o => o.vehicleId === this.selectedTriplog.vehicle.id);
        return smartConnect ? `km (${this.mapOdometerType(smartConnect)})` : 'km';
      }
      return 'km';
    }
    return '';
  }

  get fuelConsumptionType() {
    switch (this.selectedTriplog.vehicleSettings.fuelConsumptionType) {
      case FuelConsumptionType.MEASURED: return 'Gemessen';
      case FuelConsumptionType.CALCULATED: return 'Berechnet';
      default: return '-'
    }
  }

  get fuelUnit() {
    switch (this.selectedTriplog.vehicleSettings.fuelUnit) {
      case FuelUnit.LITER: return 'Liter';
      case FuelUnit.KWH: return 'kWh';
      case FuelUnit.KG: return 'kg';
      default: return '-';
    }
  }

  private mapOdometerType(value: SmartConnectVehicle) {
    switch (value.odometerType) {
      case SmartConnectVehicleOdometerTypeEnum.Gps: return 'GPS';
      case SmartConnectVehicleOdometerTypeEnum.Can: return 'Ausgelesen';
      default: return 'Unbekannt';
    }
  }

  get deviceIdExists() {
    return this.selectedTriplog.smartConnected && this.selectedTriplog.vehicle.deviceId;
  }

  private changeVehicle() {
    this.showAllSuggestionsDisabled = false;

    if (this.deviceIdExists) {
      this.fetchDevice({ deviceId: this.selectedTriplog.vehicle.deviceId })
    }
  }

  get backButton() {
    return { text: 'Zurück zu Anpassen', location: { name: 'mySettings' } };
  }

  get errorLoading() {
    return this.deviceStatus === LoadState.Error || this.deviceStatus === LoadState.Unauthorized ||
      this.triplogsStatus === LoadState.Error || this.triplogsStatus === LoadState.Unauthorized ||
      this.smartConnectVehiclesStatus === LoadState.Error || this.smartConnectVehiclesStatus === LoadState.Unauthorized;
  }

  get serialLoading() {
    return this.deviceStatus === LoadState.Loading;
  }

  get smartConnectVehiclesLoading() {
    return this.smartConnectVehiclesStatus === LoadState.Loading;
  }

  get triplogsLoaded() {
    return this.triplogsStatus === LoadState.Success;
  }

  private async inputFuelStopSuggestions(value: boolean) {
    this.suggestionsDisabled = true;
    this.errorMessage = '';

    try {
      await this.setFuelStopSuggestions({ vehicleId: this.selectedTriplog.vehicle.id, fuelStopSuggestions: value });
    } catch {
      this.errorMessage = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
    } finally {
      this.suggestionsDisabled = false;
    }
  }

  private async showAllSuggestions() {
    this.showAllSuggestionsDisabled = true;
    this.errorMessage = '';

    try {
      await this.showAllSuggestedFuelStops({ vehicleId: this.selectedTriplog.vehicle.id });
    } catch {
      this.showAllSuggestionsDisabled = false;
      this.errorMessage = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
    }
  }
}
